'use client';

import { useEffect, useRef, useState, type ReactNode } from 'react';
import Link from 'next/link';
import { AlertCircle, Calculator, Camera, CheckCircle2, LockKeyhole, Sparkles, UserRound } from 'lucide-react';
import type { SeoLandingToolType } from '@/data/seoLandingPages';
import PublicResultActions from './PublicResultActions';
import { trackGrowthEvent } from '@/lib/analytics';

interface PublicSeoToolProps {
    type: SeoLandingToolType;
    pageTitle: string;
}

interface ReducedNumber {
    value: number;
    trail: number[];
}

const masterNumbers = [11, 22, 33];

const lifePathMeanings: Record<number, string> = {
    1: 'Independent starter energy: leadership, initiative, and learning how to act without waiting for permission.',
    2: 'Sensitive connector energy: cooperation, emotional intelligence, patience, and learning how to trust timing.',
    3: 'Creative communicator energy: expression, optimism, storytelling, and turning emotion into something shareable.',
    4: 'Builder energy: structure, discipline, consistency, and creating safety through practical systems.',
    5: 'Explorer energy: freedom, change, adaptability, and learning how to choose growth without scattering focus.',
    6: 'Caretaker energy: responsibility, beauty, devotion, and balancing service with healthy boundaries.',
    7: 'Seeker energy: depth, study, intuition, solitude, and learning to trust inner evidence.',
    8: 'Executive energy: ambition, material mastery, power, and using influence with integrity.',
    9: 'Humanitarian energy: compassion, completion, art, and turning lived experience into wisdom.',
    11: 'Master 11 carries intuitive messenger energy, with Life Path 2 as the grounded base pattern.',
    22: 'Master 22 carries practical visionary energy, with Life Path 4 as the grounded base pattern.',
    33: 'Master 33 carries teacher-healer energy, with Life Path 6 as the grounded base pattern.',
};

const readinessItems = [
    'Birth date saved',
    'Birth time available',
    'Birth place available',
    'Numerology interest selected',
    'Optional palm photo ready',
];

function sumDigits(value: number) {
    return String(value)
        .split('')
        .reduce((sum, digit) => sum + Number(digit), 0);
}

function reduceNumber(value: number, preserveMaster = true): ReducedNumber {
    const trail = [value];
    let current = value;

    while (current > 9 && !(preserveMaster && masterNumbers.includes(current))) {
        current = sumDigits(current);
        trail.push(current);
    }

    return { value: current, trail };
}

function parseDateParts(dateValue: string) {
    const [year, month, day] = dateValue.split('-').map(Number);

    if (!year || !month || !day) {
        return null;
    }

    return { year, month, day };
}

function calculateLifePath(dateValue: string) {
    const parts = parseDateParts(dateValue);

    if (!parts) {
        return null;
    }

    const month = reduceNumber(parts.month);
    const day = reduceNumber(parts.day);
    const year = reduceNumber(parts.year);
    const total = month.value + day.value + year.value;
    const lifePath = reduceNumber(total);
    const baseNumber = masterNumbers.includes(lifePath.value) ? reduceNumber(lifePath.value, false).value : lifePath.value;

    return {
        parts,
        month,
        day,
        year,
        total,
        lifePath: lifePath.value,
        baseNumber,
        trail: lifePath.trail,
    };
}

function getSunSign(month: number, day: number) {
    if ((month === 1 && day >= 20) || (month === 2 && day <= 18)) return 'Aquarius';
    if ((month === 2 && day >= 19) || (month === 3 && day <= 20)) return 'Pisces';
    if ((month === 3 && day >= 21) || (month === 4 && day <= 19)) return 'Aries';
    if ((month === 4 && day >= 20) || (month === 5 && day <= 20)) return 'Taurus';
    if ((month === 5 && day >= 21) || (month === 6 && day <= 20)) return 'Gemini';
    if ((month === 6 && day >= 21) || (month === 7 && day <= 22)) return 'Cancer';
    if ((month === 7 && day >= 23) || (month === 8 && day <= 22)) return 'Leo';
    if ((month === 8 && day >= 23) || (month === 9 && day <= 22)) return 'Virgo';
    if ((month === 9 && day >= 23) || (month === 10 && day <= 22)) return 'Libra';
    if ((month === 10 && day >= 23) || (month === 11 && day <= 21)) return 'Scorpio';
    if ((month === 11 && day >= 22) || (month === 12 && day <= 21)) return 'Sagittarius';
    return 'Capricorn';
}

function FieldLabel({ children, htmlFor }: { children: ReactNode; htmlFor: string }) {
    return <label htmlFor={htmlFor} className="text-xs font-bold uppercase tracking-[0.18em] text-accent/85">{children}</label>;
}

function ToolShell({ children, kicker, title }: { children: ReactNode; kicker: string; title: string }) {
    return (
        <section id="free-tool" className="card-feature scroll-mt-24 p-4 md:p-6">
            <div className="mb-4 flex flex-col gap-2 md:flex-row md:items-end md:justify-between">
                <div>
                    <p className="text-[11px] uppercase tracking-[0.22em] text-accent mb-1">{kicker}</p>
                    <h2 className="text-xl font-playfair text-foreground">{title}</h2>
                </div>
                <div className="inline-flex items-center gap-2 rounded-full border border-[#e8d7b0]/10 bg-white/5 px-4 py-2 text-xs font-semibold text-[#ddceb0]/72">
                    <LockKeyhole className="h-4 w-4 text-accent" />
                    No public AI call
                </div>
            </div>
            {children}
        </section>
    );
}

function CostNote() {
    return (
        <div className="card-quiet p-4 text-sm leading-6 text-[#ddceb0]/74">
            <p>
                This public preview is not powered by AI and does not call the analysis backend. Create a free account when you want AI-assisted interpretation using your saved profile context.
            </p>
        </div>
    );
}

function SignupCta({ tool }: { tool: string }) {
    return (
        <Link
            href="/signup"
            onClick={() => trackGrowthEvent('result_to_signup', { tool, action: 'unlock_analysis' })}
            className="inline-flex items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-[#d4af37] to-[#8b6914] px-5 py-3 text-sm font-bold text-[#1d1509] transition-all hover:brightness-110"
        >
            Unlock AI analysis
            <Sparkles className="h-4 w-4" />
        </Link>
    );
}

function LifePathTool() {
    const [birthDate, setBirthDate] = useState('');
    const started = useRef(false);
    const completed = useRef<number | null>(null);
    const result = calculateLifePath(birthDate);
    const resultValue = result?.lifePath;

    useEffect(() => {
        if (!result || completed.current === result.lifePath) return;
        completed.current = result.lifePath;
        trackGrowthEvent('tool_completed', { tool: 'life-path', result: result.lifePath });
    }, [result]);

    return (
        <ToolShell kicker="Free Calculator" title="Calculate your life path number">
            <div className="grid gap-6 lg:grid-cols-[0.85fr_1.15fr]">
                <div className="card-quiet p-5">
                    <div className="space-y-3">
                        <FieldLabel htmlFor="life-path-birth-date">Birth date</FieldLabel>
                        <input
                            id="life-path-birth-date"
                            type="date"
                            value={birthDate}
                            onChange={(event) => {
                                setBirthDate(event.target.value);
                                if (!started.current) {
                                    started.current = true;
                                    trackGrowthEvent('tool_started', { tool: 'life-path', placement: 'calculator' });
                                }
                            }}
                            className="experience-input w-full rounded-xl px-4 py-3 outline-none transition-colors focus:border-accent/60"
                        />
                    </div>
                    <div className="mt-5 flex flex-col gap-3 sm:flex-row sm:items-center">
                        <div className="inline-flex items-center gap-2 text-sm text-[#ddceb0]/70">
                            <Calculator className="h-4 w-4 text-accent" />
                            Month + day + year, with 11/22/33 preserved.
                        </div>
                    </div>
                </div>

                <div className="space-y-4">
                    {resultValue ? (
                        <div className="card-quiet card-selected p-5">
                            <p className="text-sm uppercase tracking-[0.2em] text-accent/90">Your public result</p>
                            <div className="mt-3 flex flex-wrap items-end gap-4">
                                <span className="font-playfair text-6xl font-bold text-[#f7ead2]">{resultValue}</span>
                                {result && result.baseNumber !== result.lifePath && (
                                    <span className="mb-2 rounded-full border border-[#e8d7b0]/10 bg-white/5 px-3 py-1 text-sm text-[#ddceb0]/75">
                                        Base pattern {result.baseNumber}
                                    </span>
                                )}
                            </div>
                            <p className="mt-4 leading-7 text-[#ddceb0]/78">{lifePathMeanings[resultValue]}</p>
                            {result && (
                                <>
                                    <div className="mt-4 grid gap-3 text-sm text-[#ddceb0]/70 sm:grid-cols-3">
                                        <span>Month: {result.month.trail.join(' -> ')}</span>
                                        <span>Day: {result.day.trail.join(' -> ')}</span>
                                        <span>Year: {result.year.trail.join(' -> ')}</span>
                                    </div>
                                    <p className="mt-3 text-sm text-[#ddceb0]/62">Total trail: {result.total} {' -> '} {result.trail.slice(1).join(' -> ') || result.lifePath}</p>
                                </>
                            )}
                        </div>
                    ) : (
                        <div className="card-quiet p-5 text-[#ddceb0]/72">
                            Add your birth date to see a deterministic numerology preview.
                        </div>
                    )}
                    {resultValue && <PublicResultActions tool="life-path" title="My Life Path Number" value={String(resultValue)} description={lifePathMeanings[resultValue]} sharePath="/life-path-number-calculator" />}
                    <CostNote />
                    {resultValue && <SignupCta tool="life-path" />}
                </div>
            </div>
        </ToolShell>
    );
}

function BirthProfileTool({ pageTitle }: { pageTitle: string }) {
    const [birthDate, setBirthDate] = useState('');
    const [birthTime, setBirthTime] = useState('');
    const [birthPlace, setBirthPlace] = useState('');
    const started = useRef(false);
    const completed = useRef<string | null>(null);
    const parts = parseDateParts(birthDate);
    const sunSign = parts ? getSunSign(parts.month, parts.day) : null;
    const resultSign = sunSign;
    const toolName = pageTitle.includes('Moon') ? 'moon-sign' : 'birth-chart';

    useEffect(() => {
        if (!sunSign || completed.current === sunSign) return;
        completed.current = sunSign;
        trackGrowthEvent('tool_completed', { tool: toolName, preview: 'sun-sign' });
    }, [sunSign, toolName]);

    const startTool = () => {
        if (started.current) return;
        started.current = true;
        trackGrowthEvent('tool_started', { tool: toolName, placement: 'calculator' });
    };

    return (
        <ToolShell kicker="Public Birth Preview" title={pageTitle.includes('Moon') ? 'Build your moon sign lead profile' : 'Build your birth chart lead profile'}>
            <div className="grid gap-6 lg:grid-cols-[0.9fr_1.1fr]">
                <div className="card-quiet p-5">
                    <div className="grid gap-4">
                        <div className="space-y-3">
                            <FieldLabel htmlFor={`${toolName}-birth-date`}>Birth date</FieldLabel>
                            <input
                                id={`${toolName}-birth-date`}
                                type="date"
                                value={birthDate}
                                onChange={(event) => { setBirthDate(event.target.value); startTool(); }}
                                className="experience-input w-full rounded-xl px-4 py-3 outline-none transition-colors focus:border-accent/60"
                            />
                        </div>
                        <div className="space-y-3">
                            <FieldLabel htmlFor={`${toolName}-birth-time`}>Birth time</FieldLabel>
                            <input
                                id={`${toolName}-birth-time`}
                                type="time"
                                value={birthTime}
                                onChange={(event) => { setBirthTime(event.target.value); startTool(); }}
                                className="experience-input w-full rounded-xl px-4 py-3 outline-none transition-colors focus:border-accent/60"
                            />
                        </div>
                        <div className="space-y-3">
                            <FieldLabel htmlFor={`${toolName}-birth-place`}>Birth place</FieldLabel>
                            <input
                                id={`${toolName}-birth-place`}
                                type="text"
                                value={birthPlace}
                                onChange={(event) => { setBirthPlace(event.target.value); startTool(); }}
                                placeholder="City, country"
                                className="experience-input w-full rounded-xl px-4 py-3 outline-none transition-colors focus:border-accent/60"
                            />
                        </div>
                    </div>
                </div>

                <div className="space-y-4">
                    <div className="card-quiet card-selected p-5">
                        <div className="flex items-start gap-3">
                            <UserRound className="mt-1 h-5 w-5 flex-none text-accent" />
                            <div>
                                <p className="text-sm uppercase tracking-[0.2em] text-accent/90">Preview available here</p>
                                <h3 className="mt-2 font-playfair text-2xl text-[#f7ead2]">
                                    {resultSign ? `${resultSign} sun sign preview` : 'Add birth date for sun sign preview'}
                                </h3>
                                <p className="mt-3 leading-7 text-[#ddceb0]/78">
                                    The public page can safely preview your sun sign and collect the details needed for a stronger chart. Exact moon sign, rising sign, houses, and full synthesis are unlocked after signup.
                                </p>
                            </div>
                        </div>
                    </div>
                    <div className="grid gap-3 sm:grid-cols-3">
                        <InfoPill complete={Boolean(birthDate)} label="Date" value={birthDate || 'Needed'} />
                        <InfoPill complete={Boolean(birthTime)} label="Time" value={birthTime || 'Improves accuracy'} />
                        <InfoPill complete={Boolean(birthPlace)} label="Place" value={birthPlace || 'Needed for chart'} />
                    </div>
                    {resultSign && <PublicResultActions tool={toolName} title="My Public Astrology Preview" value={`${resultSign} Sun`} description="This sign describes the core identity layer used as one input in a fuller birth-chart reading." sharePath={pageTitle.includes('Moon') ? '/moon-sign-calculator' : '/free-birth-chart-reading'} />}
                    <CostNote />
                    {resultSign && <SignupCta tool={toolName} />}
                </div>
            </div>
        </ToolShell>
    );
}

function InfoPill({ complete, label, value }: { complete: boolean; label: string; value: string }) {
    return (
        <div className="card-quiet p-4">
            <div className="mb-2 flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-accent/80">
                {complete ? <CheckCircle2 className="h-4 w-4" /> : <AlertCircle className="h-4 w-4" />}
                {label}
            </div>
            <p className="text-sm text-[#ddceb0]/72">{value}</p>
        </div>
    );
}

function PalmSignupGateTool() {
    const [checks, setChecks] = useState<Record<string, boolean>>({
        lighting: false,
        fullPalm: false,
        focus: false,
        relaxed: false,
    });

    const qualityScore = Object.values(checks).filter(Boolean).length;

    return (
        <ToolShell kicker="Signup-Gated Palm Reading" title="Prepare your palm photo before signup">
            <div className="grid gap-6 lg:grid-cols-[0.9fr_1.1fr]">
                <div className="card-quiet p-6">
                    <div className="flex min-h-[260px] flex-col items-center justify-center rounded-xl border border-dashed border-accent/35 p-6 text-center">
                        <LockKeyhole className="h-10 w-10 text-accent" />
                        <p className="mt-4 font-playfair text-2xl text-[#f7ead2]">Palm upload opens after signup</p>
                        <p className="mt-2 text-sm leading-6 text-[#ddceb0]/65">
                            We do not accept public palm uploads on this SEO page. Sign up first, then upload inside the protected dashboard for AI-assisted palm analysis.
                        </p>
                        <div className="mt-5">
                            <SignupCta tool="palm-preview" />
                        </div>
                    </div>
                </div>

                <div className="space-y-4">
                    <div className="card-quiet card-selected p-5">
                        <div className="flex items-center justify-between gap-4">
                            <div>
                                <p className="text-sm uppercase tracking-[0.2em] text-accent/90">Readiness score</p>
                                <h3 className="mt-2 font-playfair text-3xl text-[#f7ead2]">{qualityScore}/4 photo basics</h3>
                            </div>
                            <Camera className="h-8 w-8 text-accent" />
                        </div>
                    </div>
                    <div className="grid gap-3 sm:grid-cols-2">
                        {[
                            ['lighting', 'Bright, even lighting'],
                            ['fullPalm', 'Full palm and wrist base visible'],
                            ['focus', 'Lines are in focus'],
                            ['relaxed', 'Hand relaxed, no heavy filters'],
                        ].map(([key, label]) => (
                            <label key={key} className="card-quiet card-interactive flex cursor-pointer items-center gap-3 p-4 text-sm text-[#ddceb0]/75">
                                <input
                                    type="checkbox"
                                    checked={checks[key]}
                                    onChange={(event) => setChecks((current) => ({ ...current, [key]: event.target.checked }))}
                                    className="h-4 w-4 accent-[#d4af37]"
                                />
                                {label}
                            </label>
                        ))}
                    </div>
                    <CostNote />
                </div>
            </div>
        </ToolShell>
    );
}

function ProfileReadinessTool() {
    const [ready, setReady] = useState<Record<string, boolean>>({});
    const complete = readinessItems.filter((item) => ready[item]).length;

    return (
        <ToolShell kicker="Personalization Check" title="See what makes your horoscope personalized">
            <div className="grid gap-6 lg:grid-cols-[0.9fr_1.1fr]">
                <div className="card-quiet p-5">
                    <div className="grid gap-3">
                        {readinessItems.map((item) => (
                            <label key={item} className="card-quiet card-interactive flex cursor-pointer items-center gap-3 p-4 text-sm text-[#ddceb0]/75">
                                <input
                                    type="checkbox"
                                    checked={Boolean(ready[item])}
                                    onChange={(event) => setReady((current) => ({ ...current, [item]: event.target.checked }))}
                                    className="h-4 w-4 accent-[#d4af37]"
                                />
                                {item}
                            </label>
                        ))}
                    </div>
                </div>

                <div className="space-y-4">
                    <div className="card-quiet card-selected p-5">
                        <p className="text-sm uppercase tracking-[0.2em] text-accent/90">Profile readiness</p>
                        <h3 className="mt-2 font-playfair text-4xl text-[#f7ead2]">{complete}/5 layers ready</h3>
                        <p className="mt-3 leading-7 text-[#ddceb0]/78">
                            More context helps YourAura move beyond generic sun-sign copy. Birth time and place are the biggest upgrades for chart-aware daily guidance.
                        </p>
                    </div>
                    <CostNote />
                    <SignupCta tool="profile-readiness" />
                </div>
            </div>
        </ToolShell>
    );
}

export default function PublicSeoTool({ type, pageTitle }: PublicSeoToolProps) {
    if (type === 'life-path') {
        return <LifePathTool />;
    }

    if (type === 'birth-profile') {
        return <BirthProfileTool pageTitle={pageTitle} />;
    }

    if (type === 'palm-preview') {
        return <PalmSignupGateTool />;
    }

    return <ProfileReadinessTool />;
}
